'use client'

import React, { FC } from 'react'
import { useCustomRouter, useGetTranslations, useGetRouteSearchBarProps } from '@/root/shared-hooks'
import { SailPackage } from '@/root/shared-types'
import { Dropdown } from '@/root/components/tuuleliinid'
import { Button, Checkbox } from '@/root/ui/tuuleliinid'
import { CellContent, SearchBarCell, SearchbarDayPickers } from '@/root/modules/tuuleliinid'
import { CloseIcon } from '@/root/ui/shared/icons/CloseIcon'
import { testIds } from '@/root/shared-consts'

interface RouteSearchBarComponentProps {
  sailPackages: SailPackage[]
}

export const RouteSearchBar: FC<RouteSearchBarComponentProps> = ({ sailPackages }) => {
  const { getTranslation } = useGetTranslations()
  const { locale } = useCustomRouter()

  const {
    handleDateChange,
    selectedDate,
    isRoundTripChecked,
    onRoundTripCheck,
    dropdownProps,
    selectedRoute,
    availableDates,
    onGoToSelectTicket,
    onResetRoute,
    isDayPickerOpened,
    toggleDayPickers,
  } = useGetRouteSearchBarProps(sailPackages)

  return (
    <>
      <div className="mx-auto w-full max-w-7xl">
        <h1 className="text-primary-base mb-8 text-center text-2xl font-bold">
          {getTranslation('bookTicketBadgeTitle')}
        </h1>

        <section className="flex w-full flex-col rounded-t-2xl bg-white shadow-sm lg:grid lg:min-h-20 lg:grid-cols-[1fr_1fr_auto] lg:items-stretch lg:rounded-2xl">
          <SearchBarCell className="relative min-w-0 rounded-t-2xl lg:rounded-l-2xl lg:rounded-tr-none">
            {!dropdownProps.isOpen && (
              <button
                aria-label="Open route selection"
                className="absolute inset-0 z-10 lg:hidden"
                onClick={dropdownProps.handleToggleDropdown}
                type="button"
              />
            )}
            <CellContent title={getTranslation('searchBarWhereLabel')} contentClassName="-mt-4 lg:mt-0 lg:pt-1.5">
              <div className="relative" data-testid={testIds.search.where}>
                {selectedRoute.id && !isDayPickerOpened && (
                  <button
                    aria-label="Clear route selection"
                    className="text-primary-base absolute top-1/2 right-5 z-20 flex h-5 w-5 -translate-y-1/2 items-center justify-center lg:hidden"
                    onClick={(event) => {
                      event.stopPropagation()
                      onResetRoute()
                    }}
                    type="button"
                  >
                    <CloseIcon color="primary-base" width={12} height={12} />
                  </button>
                )}
                <Dropdown
                  variant="default"
                  {...dropdownProps}
                  selectedOption={selectedRoute}
                  handleOptionSelection={(route) => {
                    dropdownProps.handleOptionSelection(route)
                    toggleDayPickers()
                  }}
                  contextClass="route-search-dropdown"
                />
              </div>
            </CellContent>
          </SearchBarCell>

          <SearchBarCell className="relative min-w-0 lg:h-20">
            {!isDayPickerOpened && (
              <button
                aria-label="Open date selection"
                className="absolute inset-0 z-10 lg:hidden"
                onClick={toggleDayPickers}
                type="button"
              />
            )}
            <CellContent title={getTranslation('searchBarWhenLabel')} contentClassName="-mt-4 ps-5 lg:mt-0 lg:pt-1.5">
              <div data-testid={testIds.search.whenWrapper}>
              <SearchbarDayPickers
                selectedDate={selectedDate}
                availableDates={availableDates}
                isRoundTrip={isRoundTripChecked}
                isRoundTripChecked={isRoundTripChecked}
                isRoundTripDisabled={!selectedRoute.extraProps?.backwardsSailPackage}
                onDateChange={handleDateChange}
                onRoundTripCheck={onRoundTripCheck}
                roundTripLabel={getTranslation('searchBarRoundTrip')}
                titleTo={selectedRoute.name[locale]}
                titleFrom={selectedRoute.extraProps?.backwardsName && selectedRoute.extraProps?.backwardsName[locale]}
                isOpened={isDayPickerOpened}
                toggleDayPickers={toggleDayPickers}
              />
              </div>
            </CellContent>
          </SearchBarCell>

          <SearchBarCell className="relative min-w-0 lg:flex lg:h-20 lg:flex-row lg:items-stretch lg:justify-start lg:rounded-r-2xl">
            <CellContent
              title={getTranslation('searchBarRoundTrip')}
              className="mt-1.5 mb-3 grid-rows-none lg:my-0 lg:w-50 xl:w-65"
            >
              <Checkbox
                checked={isRoundTripChecked}
                variant="success"
                onChange={onRoundTripCheck}
                disabled={!selectedRoute.extraProps?.backwardsSailPackage}
                className="h-8 ps-5 lg:pt-2 rtl:items-center"
                testId={testIds.search.roundTripCheckbox}
              />
            </CellContent>

            <Button
              variant="contained"
              className="bg-base-medium-light hidden h-20 w-50 shrink-0 items-center justify-center rounded-none border-none text-xl font-bold text-white lg:flex lg:rounded-r-2xl xl:w-70"
              disabled={Boolean(!selectedDate.to)}
              onClick={onGoToSelectTicket}
              data-testid={testIds.search.goTickets}
            >
              {getTranslation('searchBarGetTickets')}
            </Button>
          </SearchBarCell>
        </section>

        <Button
          variant="gray"
          className="flex h-20 w-full items-center justify-center rounded-2xl rounded-t-none px-4 lg:hidden"
          disabled={!selectedDate.to}
          onClick={onGoToSelectTicket}
          data-testid={testIds.search.goTickets}
        >
          <div className="text-left">
            <div className="text-2xl leading-[1.2] font-bold">{getTranslation('searchBarGetTickets')}</div>
          </div>
        </Button>
      </div>
    </>
  )
}
